import { useReferenciaManoDeObra } from './usePartirDe'
import estilos from './MargenEnVivo.module.css'

type Props = {
  /** Qué porcentaje de la base es mano de obra, ahora mismo. */
  porcentaje: number
  /** De qué orden se saca la referencia. Sin ella solo se muestra el número. */
  ordenServicioId: string
}

/**
 * El margen en vivo.
 *
 * De las 2.616 cotizaciones del histórico solo 226 discriminan mano de obra:
 * el resto se mandó sin saber con cuánto quedaba la empresa. Aquí se ve
 * mientras se arma, que es cuando todavía se puede corregir.
 *
 * Se mide sobre la base y no sobre el total: el IVA se recauda y se entrega.
 */
export function MargenEnVivo({ porcentaje, ordenServicioId }: Props) {
  const { referencia } = useReferenciaManoDeObra(ordenServicioId)

  const bajo = referencia !== null && porcentaje < referencia.minimo
  const alto = referencia !== null && porcentaje > referencia.maximo
  const dentro = referencia !== null && !bajo && !alto

  // La escala llega hasta donde haga falta para que todo quepa: la banda, la
  // cifra de ahora y un poco de aire.
  const tope = Math.max(referencia?.maximo ?? 0, porcentaje) * 1.15 || 100
  const donde = (valor: number) => `${Math.min(100, (valor / tope) * 100)}%`

  const tono = bajo ? estilos.bajo : dentro ? estilos.dentro : estilos.neutro

  return (
    <div className={`${estilos.zona} ${tono}`}>
      <div className={estilos.encabezado}>
        <span className={estilos.etiqueta}>Mano de obra sobre la base</span>
        <span className={estilos.cifra}>{porcentaje.toFixed(1)} %</span>
      </div>

      <div className={estilos.barra}>
        {/* La franja donde caen las cotizaciones de referencia. */}
        {referencia && (
          <span
            className={estilos.banda}
            style={{
              left: donde(referencia.minimo),
              width: `calc(${donde(referencia.maximo)} - ${donde(referencia.minimo)})`,
            }}
            aria-hidden="true"
          />
        )}

        <span className={estilos.aguja} style={{ left: donde(porcentaje) }} aria-hidden="true" />
      </div>

      {referencia && (
        <p className={estilos.referencia}>
          {bajo
            ? `Por debajo de ${referencia.minimo} %, que es ${referencia.ambito}`
            : alto
              ? `Por encima de ${referencia.maximo} %, que es ${referencia.ambito}`
              : `Dentro de ${referencia.minimo} % a ${referencia.maximo} %, que es ${referencia.ambito}`}
        </p>
      )}
    </div>
  )
}
